// TASK 9 — Auto-nudge: ร่างข้อความทวง/แจ้งเอกสาร
// export-side (Phyto/CO/invoice) → ทวง supplier (ไทย/อังกฤษตามประเทศ)
// import-side (สมอ./ป่าไม้/อย./สรรพสามิต/ใบขน) → แจ้ง broker (เช่น Yusen) ให้ดำเนินการ
// อื่นๆ (booking/ใบตรวจรับ) → งานภายในของเรา

import { reqLabel } from './reqtypes.js';

const TH_ORIGINS = new Set(['TH']);

// เอกสารที่ supplier ต้นทางเป็นคนออก/ส่ง
const SUPPLIER_DOCS = new Set([
  'proforma_invoice', 'commercial_invoice', 'packing_list',
  'certificate_of_origin', 'phytosanitary', 'fumigation',
]);
// เอกสารฝั่งเรา (ฝ่ายบัญชี/การเงินแนบ — ไม่ใช่ทวงใคร)
const ACCOUNTING_DOCS = new Set(['payment_proof']);
// มาตรการ/เอกสารในไทยที่ broker เดินให้ (ยื่นหน่วยงานไทย / ทำใบขน)
const BROKER_DOCS = new Set([
  'permit_tisi', 'permit_forest', 'permit_fda', 'permit_excise',
  'import_license_other', 'declaration', 'duty_receipt',
]);

export function draftNudge({ requirement, shipment, consignment }) {
  const rt = requirement.req_type;
  const doc = reqLabel(rt);
  const brokerName = shipment.forwarder || 'broker';

  // ── หลักฐานโอน/SWIFT = งานฝ่ายบัญชี/การเงิน ──
  if (ACCOUNTING_DOCS.has(rt)) return {
    audience: 'internal', to: null, channel: null,
    title: `งานฝ่ายบัญชี/การเงิน: ${doc}`,
    body: `[ภายใน] ${doc} — ให้ฝ่ายบัญชี/การเงินแนบหลักฐานการโอน (บัญชีเป็นคนโอนเงินให้ ${consignment?.supplier_name ?? 'supplier'}) ` +
      `ไม่ใช่ทวง supplier · ตู้ ${shipment.code}`,
  };

  // ── ฝั่งในไทย → แจ้ง broker ให้ทำ (เช่น Yusen) ──
  const isBroker = requirement.side === 'import' || BROKER_DOCS.has(rt);
  const isSupplier = requirement.side === 'export' || SUPPLIER_DOCS.has(rt);

  if (isBroker && !isSupplier) {
    const body =
      `เรียน ${brokerName}\n\n` +
      `รบกวนดำเนินการ "${doc}" สำหรับตู้ ${shipment.code}` +
      `${consignment ? ` (invoice ${consignment.invoice_no ?? '-'} · ${consignment.supplier_name})` : ''} ด้วยครับ\n` +
      `ต้องได้${whenLabel(requirement.needed_by)}` +
      `${requirement.remind_before ? ' (ควรเริ่มยื่นแต่เนิ่นๆ)' : ''}` +
      `${shipment.eta ? ` — ETA ${shipment.eta}` : ''}\n` +
      `หากต้องใช้เอกสารเพิ่มจากเรา แจ้งได้เลยครับ ขอบคุณครับ`;
    return {
      audience: 'broker', to: brokerName, channel: 'draft', lang: 'th',
      title: `แจ้ง ${brokerName} ให้ทำ ${doc}`, body,
    };
  }

  // ── ฝั่งต้นทาง → ทวง supplier ──
  if (isSupplier) {
    const toThai = TH_ORIGINS.has((consignment?.origin_country ?? '').toUpperCase());
    const supplier = consignment?.supplier_name ?? 'supplier';
    // กฎที่ยังให้ยืนยันกับชิปปิ้ง (เช่น เกษตร: Phyto หรือ พ.ก.5) ห้ามบอกซัพว่าเคลียร์ไม่ได้แน่ ๆ
    const unsure = /ฝากยืนยัน/.test(requirement.conditional_reason ?? '');
    const body = toThai
      ? `เรียน ${supplier}\n\nรบกวนขอเอกสาร "${doc}" สำหรับ invoice ${consignment?.invoice_no ?? ''} ` +
        `(ตู้ ${shipment.code}) ด้วยค่ะ ต้องได้${whenLabel(requirement.needed_by)} ` +
        `${requirement.remind_before ? 'และออกย้อนหลังไม่ได้ จึงต้องเริ่มขอแต่เนิ่นๆ ' : ''}` +
        `${unsure ? '' : 'ถ้าของถึงแล้วยังไม่มีจะเคลียร์ศุลกากรไม่ได้ค่ะ '}ขอบคุณค่ะ`
      : `Dear ${supplier},\n\nCould you please provide the "${doc}" for invoice ${consignment?.invoice_no ?? ''} ` +
        `(shipment ${shipment.code})? We need it before ${whenLabelEn(requirement.needed_by)}` +
        `${requirement.remind_before ? ', and it cannot be issued retroactively, so please start early' : ''}. ` +
        `${unsure ? '' : `Without it we cannot clear customs on arrival${shipment.eta ? ` (ETA ${shipment.eta})` : ''}. `}Thank you.`;
    return {
      audience: 'supplier', to: supplier, channel: 'draft', lang: toThai ? 'th' : 'en',
      title: `ทวง ${doc} จาก ${supplier}`, body,
    };
  }

  // ── อื่นๆ = งานภายในของเรา ──
  return {
    audience: 'internal', to: null, channel: null,
    title: `งานของเรา: ${doc}`,
    body: `[ภายใน] ${doc} เป็นงานที่เราจัดการเอง — ตู้ ${shipment.code}. ` +
      `กำหนด: ${whenLabel(requirement.needed_by)}.`,
  };
}

const whenLabel = (nb) => ({
  before_pay: 'ก่อนจ่ายเงิน', before_ship: 'ก่อนของออกจากต้นทาง', before_pickup: 'ก่อนรับของ',
  before_clear: 'ก่อนเดินพิธีศุลกากร', on_arrival: 'ตอนของถึง',
}[nb] ?? nb);
const whenLabelEn = (nb) => ({
  before_pay: 'payment', before_ship: 'shipment departs', before_pickup: 'pickup',
  before_clear: 'customs clearance', on_arrival: 'arrival',
}[nb] ?? nb);
